import { useEffect, useRef } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { useCommentsTracking } from './useCommentsTracking'

type UseCommunityLoadedTrackingProps = {
  unansweredCommentsCount: number
  /** Omitted when mention counts are disabled, so the event field stays absent. */
  unansweredMentionsCount?: number
  loading?: boolean
}

/**
 * Fires the "Community Loaded" Segment event once per mount, after the
 * unanswered comment count for the current organization is known.
 *
 * Mounted at the `/comments/*` and `/community/*` route boundary so entering
 * the Community section triggers the event regardless of which navigation
 * layout is active (pre- or post-Visual Refresh).
 */
export function useCommunityLoadedTracking({
  unansweredCommentsCount,
  unansweredMentionsCount,
  loading = false,
}: UseCommunityLoadedTrackingProps): void {
  const organizationId = useOrganizationId()
  const hasTrackedRef = useRef(false)
  const { trackCommunityLoaded } = useCommentsTracking()

  useEffect(() => {
    if (hasTrackedRef.current || !organizationId || loading) {
      return
    }

    trackCommunityLoaded({ unansweredCommentsCount, unansweredMentionsCount })
    hasTrackedRef.current = true
  }, [
    organizationId,
    unansweredCommentsCount,
    unansweredMentionsCount,
    loading,
    trackCommunityLoaded,
  ])
}
